import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaEye, FaSearch, FaStar, FaUserPlus, FaTrash, FaEdit, FaChevronRight, FaBan, FaCheckCircle } from 'react-icons/fa';
import api from '../../utils/api.service';
import { ENDPOINTS } from '../../utils/api-config';
import { toast } from 'react-toastify';
import './Customers.css';
import Pagination from '../Common/Pagination';
import ConfirmModal from '../Common/ConfirmModal';
import SuspendModal from '../Common/SuspendModal';

const CustomersList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'suspended'
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    id: null
  });

  const [suspendModal, setSuspendModal] = useState({
    isOpen: false,
    id: null,
    name: ''
  });

  const [activateModal, setActivateModal] = useState({
    isOpen: false,
    id: null
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [inputValue]);

  const getFullUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    const baseUrl = ENDPOINTS.AUTH.LOGIN.replace('/api/auth/login', '').replace('/auth/login', '');
    return `${baseUrl}/${url.startsWith('/') ? url.slice(1) : url}`;
  };

  const fetchCustomers = useCallback(async () => {
    if (isFirstLoad) setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm || undefined,
        isBlocked: activeTab === 'suspended' ? true : false
      };
      const response = await api.get(ENDPOINTS.CUSTOMERS.base, { params });
      const customersData = response.data || [];
      setCustomers(customersData.data || customersData);
      setPagination(prev => ({ ...prev, total: customersData.meta?.total || response.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, activeTab, isFirstLoad]);

  const handleDelete = (e, id) => {
    e.stopPropagation();
    if (!id) return;
    setDeleteModal({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const { id } = deleteModal;
    try {
      await api.delete(`${ENDPOINTS.CUSTOMERS.base}/${id}`);
      toast.success('Customer profile permanently deleted');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer.');
    }
  };

  const handleSuspend = (e, customer) => {
    e.stopPropagation();
    setSuspendModal({
      isOpen: true,
      id: customer.id,
      name: `${customer.firstName} ${customer.lastName}`
    });
  };

  const confirmSuspend = async (reason) => {
    try {
      await api.post(`${ENDPOINTS.CUSTOMERS.base}/${suspendModal.id}/block`, {
        reason,
        isBlocked: true
      });
      toast.success('Member account has been suspended');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to suspend member');
    }
  };

  const handleActivate = (e, id) => {
    e.stopPropagation();
    setActivateModal({ isOpen: true, id });
  };

  const confirmActivate = async () => {
    try {
      await api.post(`${ENDPOINTS.CUSTOMERS.base}/${activateModal.id}/unblock`);
      toast.success('Member account reactivated');
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to reactivate member');
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  if (loading && isFirstLoad) {
    return (
      <div className="loading-spinner-container">
        <div className="modern-spinner"></div>
        <p className="loading-text">Fetching platform members...</p>
      </div>
    );
  }

  return (
    <div className="customers-page">
      {/* PROFESSIONAL HEADER */}
      <div className="page-header">
        <div>
          <h1>Customers Management</h1>
          <p className="page-subtitle">Manage all customers and their platform interactions with detailed analytics.</p>
        </div>
        {/* <div className="page-actions">
          <button onClick={() => navigate('/customers/create')} className="btn btn-primary">
            <FaUserPlus /> Create Customer
          </button>
        </div> */}
      </div>

      {/* PREMIUM STATS TILES */}
      {/* <div className="stats-modern">
        <div className="stat">
          <FaStar />
          <div>
            <h3>{pagination.total || customers.length}</h3>
            <p>Active Members</p>
          </div>
        </div>
        
        <div className="stat">
          <FaUserPlus />
          <div>
            <h3>+12%</h3>
            <p>Monthly Growth</p>
          </div>
        </div>

        <div className="stat">
          <FaChevronRight />
          <div>
            <h3>Enterprise</h3>
            <p>Current Plan</p>
          </div>
        </div>
      </div> */}

      {/* UNIFIED LIST HEADER & SEARCH */}
      <div className="content-card modern filters-container-premium">
        <div className="filters-header-row">
          <div className="list-title-group">
            <h3 className="list-title">All Registered Customers</h3>
            <span className="list-count">{pagination.total} platform records identified</span>
          </div>

          <div className="search-bar-premium">
            <div style={{ position: 'relative', width: '100%', display: 'flex', alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Search by name, email, or ID..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="search-input"
                style={{ width: '100%', paddingRight: '30px' }}
              />
              {inputValue && (
                <button
                  onClick={() => setInputValue('')}
                  style={{ position: 'absolute', right: '8px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0', fontSize: '16px', display: 'flex', alignItems: 'center' }}
                  title="Clear search"
                >
                  &#x2715;
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="tabs-container-premium">
          <button
            className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Active Members
          </button>
          <button
            className={`tab-btn suspended-tab ${activeTab === 'suspended' ? 'active' : ''}`}
            onClick={() => setActiveTab('suspended')}
          >
            Suspended
          </button>
        </div>
      </div>

      {/* CUSTOMERS TABLE - PROFESSIONAL UI */}
      <div className="content-card modern table-card-premium" style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact Details</th>
                {/* <th>Loyalty Pts</th> */}
                <th>Total Bookings</th>
                <th>Lifetime Spent</th>
                <th>Registration Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id} onClick={() => navigate(`/customers/${customer.id}`)}>
                  <td>
                    <div className="user-cell">
                      <img
                        src={getFullUrl(customer.avatar) || `https://ui-avatars.com/api/?name=${customer.firstName}+${customer.lastName}&background=50C8AC&color=fff`}
                        alt="avatar"
                        className="user-avatar-img"
                      />
                      <span style={{ fontWeight: 700 }}>{customer.firstName} {customer.lastName}</span>
                    </div>
                  </td>
                  <td>
                    <div className="contact-cell">
                      <span>{customer.email}</span>
                      <span className="contact-phone">{customer.phone}</span>
                    </div>
                  </td>
                  {/* <td>
                    <span className="points-badge">{customer.loyaltyPoints || 0} Pts</span>
                  </td> */}
                  <td>
                    <span style={{ fontWeight: 600 }}>{customer.totalBookings || 0}</span>
                  </td>
                  <td>
                    <span className="amount">${Number(customer.totalSpent || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </td>
                  <td>
                    <span className="joined-date">
                      {new Date(customer.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers/${customer.id}`); }}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="action-btn edit"
                        onClick={(e) => { e.stopPropagation(); navigate(`/customers/${customer.id}/edit`); }}
                      >
                        <FaEdit />
                      </button>
                      {activeTab === 'active' ? (
                        <button
                          className="action-btn suspend"
                          title="Suspend Member"
                          onClick={(e) => handleSuspend(e, customer)}
                        >
                          <FaBan />
                        </button>
                      ) : (
                        <button
                          className="action-btn activate"
                          title="Reactivate Member"
                          onClick={(e) => handleActivate(e, customer.id)}
                        >
                          <FaCheckCircle />
                        </button>
                      )}
                      {/* <button 
                        className="action-btn delete"
                        onClick={(e) => handleDelete(e, customer.id)}
                      >
                        <FaTrash />
                      </button> */}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ padding: '20px', borderTop: '1px solid #f1f5f9' }}>
          <Pagination
            currentPage={pagination.page}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
          />
        </div>
      </div>

      {/* MODALS */}
      <SuspendModal
        isOpen={suspendModal.isOpen}
        onClose={() => setSuspendModal({ isOpen: false, id: null, name: '' })}
        onConfirm={confirmSuspend}
        memberName={suspendModal.name}
        title="Account Suspension"
        message="Please provide a reason for suspending this member's access. They will be notified of this reason."
      />

      <ConfirmModal
        isOpen={activateModal.isOpen}
        onClose={() => setActivateModal({ isOpen: false, id: null })}
        onConfirm={confirmActivate}
        title="Reactivate Member Account?"
        message="This will restore the member's access to all platform features and mobile app login."
        confirmText="Reactivate Account"
        type="warning"
      />

      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Customer Profile?"
        message="Warning: This will permanently delete the customer and all their associated bookings, children, reviews, tickets, and favorites. This action cannot be undo."
        confirmText="Permanently Delete"
      />
    </div>
  );
};

export default CustomersList;